import React, { useCallback, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';

import type { IAssistantVoiceByLanguage } from '@shared/interfaces/assistantScripts';
import {
  DEFAULT_ENGINE,
  DEFAULT_VOICE_ID,
  POLLY_LANGUAGE_LABELS,
  PollyEngine,
  PollyLanguage,
  testAudioText,
} from '@shared/interfaces/polly';
import {
  getAvailableEngines,
  getVoices,
} from '@shared/components/AssistantScriptDialog/constants';
import useAudioPreview from '@shared/components/AssistantScriptDialog/hooks/useAudioPreview';

import AssistantQueryService from '@services/AssistantQueryService';

import { useAiScriptsTreeStyles } from './styles';

export type AssistantVoices = Partial<
  Record<PollyLanguage, IAssistantVoiceByLanguage>
>;

// The project level offers female voices only, and the configuration is an
// override of that same choice — a wider list here would let a configuration
// pick a voice the project could never have.
const GENDER = 'female';

interface VoiceSectionProps {
  /** Language the project's scripts are authored in. */
  baseLanguage: PollyLanguage;
  /** Additional languages the project's scripts are translated into. */
  languages: PollyLanguage[];
  /** Voices the project resolved for those languages. */
  projectVoices: AssistantVoices;
  /** Voices this configuration overrides, keyed by language. */
  voices: AssistantVoices;
  /** Name the assistant introduces itself with in the audition line. */
  assistantName: string;
  disabled: boolean;
  /** Called with the configuration's complete new override map. */
  onChange: (voices: AssistantVoices) => void;
}

/**
 * Resolves the voice a language actually plays in, following the same order
 * the hub uses at runtime: this configuration, then the project, then the
 * best female voice the language has.
 *
 * @param language - Language to resolve.
 * @param voices - Overrides set on this configuration.
 * @param projectVoices - Voices configured at project level.
 * @returns The engine and voice that will be heard.
 */
const resolveVoice = (
  language: PollyLanguage,
  voices: AssistantVoices,
  projectVoices: AssistantVoices,
): { engine: PollyEngine; voiceId: string } => {
  const chosen = voices[language] ?? projectVoices[language];
  const engine =
    (chosen?.engine as PollyEngine | undefined) ??
    getAvailableEngines(language, GENDER)[0] ??
    DEFAULT_ENGINE;
  const voiceId =
    chosen?.voiceId ??
    getVoices(language, engine, GENDER)[0] ??
    DEFAULT_VOICE_ID;

  return { engine, voiceId };
};

/**
 * "Assistant voice" block of the General tab: one row per language the
 * project's scripts exist in, each overriding the project's voice for that
 * language only.
 *
 * Which languages are listed stays a project decision — the scripts were
 * written and translated against them — so a configuration may change how the
 * assistant sounds, never what it can say.
 */
const VoiceSection = ({
  baseLanguage,
  languages,
  projectVoices,
  voices,
  assistantName,
  disabled,
  onChange,
}: VoiceSectionProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiScriptsTreeStyles();

  const rows = useMemo(() => {
    const ordered = [baseLanguage, ...languages];

    return ordered.filter((lang, index) => ordered.indexOf(lang) === index);
  }, [baseLanguage, languages]);

  // What the audition hook resolves against: the configuration's overrides on
  // top of the project's voices, so each row previews exactly what it shows.
  const effectiveVoices = useMemo(
    () =>
      rows.reduce<AssistantVoices>((acc, lang) => {
        acc[lang] = resolveVoice(lang, voices, projectVoices);

        return acc;
      }, {}),
    [rows, voices, projectVoices],
  );

  const handleGenerateAudio = useCallback(
    (params: {
      text: string;
      voiceId: string;
      engine: string;
      language?: string;
    }) =>
      AssistantQueryService.generateAssistantAudio({
        ...params,
        disableOptimizations: true,
      }),
    [],
  );

  const { audioPlaybackKey, isAudioPlaying, playAudio, stopAudio } =
    useAudioPreview({
      activeLanguage: baseLanguage,
      effectiveBaseLanguage: baseLanguage,
      engine: effectiveVoices[baseLanguage]?.engine,
      voiceId: effectiveVoices[baseLanguage]?.voiceId,
      voicesByLanguage: effectiveVoices,
      onGenerateAudio: handleGenerateAudio,
    });

  /**
   * Writes one language's voice into the override map.
   *
   * @param language - Language the row configures.
   * @param patch - Engine and/or voice to store for it.
   */
  const setVoice = (
    language: PollyLanguage,
    patch: Partial<IAssistantVoiceByLanguage>,
  ) => {
    const current = resolveVoice(language, voices, projectVoices);

    onChange({ ...voices, [language]: { ...current, ...patch } });
  };

  /**
   * Drops one language's override so it follows the project again.
   *
   * @param language - Language to reset.
   */
  const resetVoice = (language: PollyLanguage) => {
    const next = { ...voices };
    delete next[language];

    onChange(next);
  };

  return (
    <Box sx={classes.generalField} data-testid="ai-tree-general-voice-section">
      <Typography sx={classes.generalLabel}>
        {translate('editorMode.aiGeneralVoice', 'Assistant voice')}
      </Typography>

      {rows.map((lang) => {
        const { engine, voiceId } = resolveVoice(lang, voices, projectVoices);
        const availableEngines = getAvailableEngines(lang, GENDER);
        const availableVoices = getVoices(lang, engine, GENDER);
        const isOverridden = !!voices[lang];
        const playbackKey = `lang:${lang}` as const;
        const isRowPlaying = isAudioPlaying && audioPlaybackKey === playbackKey;

        return (
          <Box
            key={lang}
            sx={classes.generalVoiceRow}
            data-testid={`ai-tree-general-voice-row-${lang}`}
          >
            <Box sx={classes.generalVoiceHeader}>
              <Typography sx={classes.generalVoiceLanguage}>
                {POLLY_LANGUAGE_LABELS[lang]}
              </Typography>
              {isOverridden ? null : (
                <Typography sx={classes.generalVoiceInherited}>
                  {translate('editorMode.aiGeneralVoiceInherited', 'Project')}
                </Typography>
              )}
            </Box>

            <Box sx={classes.generalVoiceControls}>
              <Select
                size="small"
                value={engine}
                disabled={disabled}
                onChange={(changeEvent) => {
                  const nextEngine = changeEvent.target.value as PollyEngine;
                  // A voice belongs to one engine, so switching engines has to
                  // carry the voice to one that exists there.
                  const nextVoices = getVoices(lang, nextEngine, GENDER);

                  setVoice(lang, {
                    engine: nextEngine,
                    voiceId: nextVoices.includes(voiceId)
                      ? voiceId
                      : nextVoices[0] ?? DEFAULT_VOICE_ID,
                  });
                }}
                sx={classes.generalVoiceSelect}
                inputProps={{
                  'data-testid': `ai-tree-general-voice-engine-${lang}`,
                }}
              >
                {availableEngines.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                value={voiceId}
                disabled={disabled}
                onChange={(changeEvent) =>
                  setVoice(lang, { voiceId: changeEvent.target.value })
                }
                sx={classes.generalVoiceSelect}
                inputProps={{
                  'data-testid': `ai-tree-general-voice-voice-${lang}`,
                }}
              >
                {availableVoices.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size="small"
                sx={classes.generalVoiceIconButton}
                onClick={() =>
                  isRowPlaying
                    ? stopAudio()
                    : playAudio(
                        testAudioText[lang](
                          assistantName || translate('assistant.noa'),
                        ),
                        playbackKey,
                        lang,
                      )
                }
                data-testid={`ai-tree-general-voice-test-${lang}`}
              >
                {audioPlaybackKey === playbackKey && !isAudioPlaying ? (
                  <CircularProgress size={13} sx={classes.generalSpinner} />
                ) : isRowPlaying ? (
                  <StopIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>

            {isOverridden ? (
              <Box
                component="button"
                type="button"
                disabled={disabled}
                sx={classes.generalVoiceReset}
                onClick={() => resetVoice(lang)}
                data-testid={`ai-tree-general-voice-reset-${lang}`}
              >
                {translate(
                  'editorMode.aiGeneralVoiceReset',
                  'Use project voice',
                )}
              </Box>
            ) : null}
          </Box>
        );
      })}

      <Typography sx={classes.generalHelper}>
        {translate(
          'editorMode.aiGeneralVoiceHint',
          'Overrides the project voice for this configuration only. Languages come from the project.',
        )}
      </Typography>
    </Box>
  );
};

export default VoiceSection;
